import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw, Camera, ImageIcon, Bug } from 'lucide-react';

const STORAGE_KEY = 'agriagent.chat.session';

const Chatbot = ({ farmer, farm, crops, selectedCrop, season }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null); // { dataUrl, name }
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const sessionId = useMemo(() => {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) { sid = 'chat-' + Math.random().toString(36).slice(2, 12); localStorage.setItem(STORAGE_KEY, sid); }
    return sid;
  }, []);

  const cropLabel = crops?.find(c => c.key === selectedCrop)?.label;

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/chat/history?session_id=${sessionId}`)
      .then(r => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if ((!text && !photo) || sending) return;
    setSending(true);
    // Optimistically add the user message (with inline photo if any)
    const userMsg = {
      role: 'user',
      content: text || (photo ? '📸 Attached a crop photo' : ''),
      image: photo?.dataUrl,
      timestamp: new Date().toISOString(),
    };
    setMessages(m => [...m, userMsg]);
    const currentPhoto = photo;
    setInput('');
    setPhoto(null);

    try {
      if (currentPhoto) {
        // Route to Gemini 3 Flash vision endpoint for pest / disease / deficiency diagnosis
        const diag = await api.photoDiagnosis({
          image_base64: currentPhoto.dataUrl,
          question: text || null,
          crop_label: cropLabel || null,
          farm_id: farm?.id || null,
          farmer_id: farmer?.id || null,
          season: season || null,
        });
        setMessages(m => [...m, {
          role: 'assistant',
          content: formatDiagnosis(diag),
          diagnosis: diag,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        const { data } = await axios.post(`${API}/chat`, {
          session_id: sessionId,
          message: text,
          farm_id: farm?.id || null,
          farmer_id: farmer?.id || null,
          crop_label: cropLabel || null,
          season: season || null,
        });
        setMessages(m => [...m, { role: 'assistant', content: data.reply, timestamp: data.timestamp }]);
      }
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: '_(chat request failed — please try again)_', timestamp: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const formatDiagnosis = (d) => {
    if (!d) return 'Diagnosis unavailable.';
    const conf = d.confidence ? ` (${d.confidence} confidence)` : '';
    const sev  = d.severity && d.severity !== 'unclear' ? ` · severity: ${d.severity}` : '';
    const acts = (d.actions || []).map((a, i) => `${i + 1}. ${a}`).join('\n');
    const src = d.source === 'gemini' ? '\n\n_Analysed by Gemini 3 Flash · verify against KVK bulletin_' : '';
    return `🔍 **${d.diagnosis_label || 'Diagnosis'}**${conf}${sev}\n\n${d.explanation || ''}\n\n${acts}${src}`;
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      alert('Please pick a photo under 4 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ dataUrl: reader.result, name: f.name });
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <>
      {/* Floating launcher (above the weather bar) */}
      <button
        data-testid="open-chatbot-btn"
        onClick={() => setOpen(true)}
        className={`fixed bottom-24 right-5 z-40 rounded-full h-14 w-14 shadow-lg flex items-center justify-center
                    text-white bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))]
                    transition-transform ${open ? 'scale-0' : 'scale-100'}`}
        aria-label="Open chatbot"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-20 right-3 sm:right-5 z-50 w-[95vw] sm:w-[400px] h-[70vh] sm:h-[560px]
                     bg-white rounded-2xl border border-icrisat-green/30 shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <header className="bg-icrisat-green text-white px-4 py-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-base leading-tight">Ask Me Questions</div>
              <div className="text-xs opacity-80">Ask any farm question · attach a photo for diagnosis</div>
            </div>
            <button onClick={reset} data-testid="chatbot-reset" title="Reset conversation" className="opacity-80 hover:opacity-100">
              <RotateCcw className="h-4 w-4" />
            </button>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close" className="opacity-90 hover:opacity-100">
              <X className="h-5 w-5" />
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-icrisat-cream/50">
            {messages.length === 0 && (
              <div className="rounded-lg bg-white border border-border/60 p-3 text-sm text-icrisat-earth">
                Namaste 🙏 I'm your AgriAgent assistant. Ask me about crops,
                sowing calendars, PJTSAU practices, MSP, Rythu Bandhu, PMFBY —
                anything a smallholder farmer in Telangana needs.
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {['When to sow rice in Warangal?', 'PMFBY deadline?', 'Kisan Credit Card ఎలా apply?'].map(q => (
                    <button
                      key={q}
                      className="text-xs px-2 py-1 rounded-full bg-icrisat-green-soft border border-icrisat-green/30 text-icrisat-green hover:bg-icrisat-green hover:text-white"
                      onClick={() => setInput(q)}
                    >{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={`${m.timestamp}-${i}`} role={m.role} content={m.content} image={m.image} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-icrisat-earth text-xs pl-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                {photo || messages[messages.length - 1]?.image
                  ? <>Gemini is examining your crop photo…</>
                  : <>AgriAgent is thinking…</>}
              </div>
            )}
          </div>

          <div className="border-t border-border/60 p-2 bg-white">
            {photo && (
              <div className="mb-2 flex items-center gap-2 bg-icrisat-green-soft border border-icrisat-green/30 rounded-lg p-2">
                <img src={photo.dataUrl} alt="crop preview" className="h-12 w-12 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-icrisat-green font-medium truncate">{photo.name}</p>
                  <p className="text-[11px] text-muted-foreground">Photo ready — ask a question and send</p>
                </div>
                <button
                  onClick={() => setPhoto(null)}
                  data-testid="chatbot-photo-remove"
                  className="text-muted-foreground hover:text-icrisat-green p-1"
                  aria-label="Remove photo"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPhoto}
                className="hidden"
                data-testid="chatbot-photo-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="chatbot-photo-btn"
                aria-label="Add crop photo"
                className="h-10 w-10 rounded-md border border-icrisat-green/40 text-icrisat-green
                           hover:bg-icrisat-green hover:text-white flex items-center justify-center transition-colors shrink-0"
              >
                {photo ? <ImageIcon className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
              </button>
              <Input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={photo ? "Describe the crop issue…" : "Ask any farming question…"}
                disabled={sending}
              />
              <Button
                data-testid="chatbot-send"
                onClick={send} disabled={sending || (!input.trim() && !photo)}
                className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Bubble = ({ role, content, image }) => {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed
                       ${isUser
                          ? 'bg-icrisat-green text-white rounded-br-sm'
                          : 'bg-white border border-border/60 text-icrisat-ink rounded-bl-sm'}`}>
        {image && (
          <img
            src={image}
            alt="attached crop"
            className="mb-2 rounded-lg max-h-40 object-cover"
            data-testid="chatbot-bubble-image"
          />
        )}
        {content}
      </div>
    </div>
  );
};

export default Chatbot;
